<template>
    <v-input ref="v-input" :rules="getRules" :value="value" validate-on-blur :error-messages="errorMessages">
        <v-layout wrap align-center>
            <v-flex xs4>
                <calender-text-field
                    v-model="day"
                    :rules="[rules.YYYYMMDDPattern]"
                    :disabled="isDisabled"
                    :max="calenderMax"
                    :min="calenderMin"
                    dense
                    @focus="isDayFocused = true"
                    @blur="isDayFocused = false"
                    @update:error="e => (calenderError = e)"
                />
            </v-flex>
            <v-spacer />
            <v-flex xs7>
                <time-picker-changed-by-browser
                    v-model="time"
                    :disabled="isDisabled"
                    :rules="[rules.HHMMPattern]"
                    @focus="isTimeFocused = true"
                    @blur="isTimeFocused = false"
                    @update:error="e => (timeError = e)"
                />
            </v-flex>
        </v-layout>
    </v-input>
</template>

<script lang="ts">
import { mixins } from 'vue-class-component';
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import CalenderTextField from './CalenderTextField.vue';
import InputValidation from '../../mixins/InputValidation';
import { AppContext } from '../../store/context';
import TimePickerChangedByBrowser from '../molecules/general/TimePickerChangedByBrowser.vue';
import dayjs from 'dayjs';

@Component({
    name: 'DateTimeField',
    components: {
        CalenderTextField,
        TimePickerChangedByBrowser,
    },
})
export default class DateTimeField extends mixins(InputValidation) {
    @Prop({
        default: undefined,
    })
    readonly value?: Date;
    @Prop({ default: false }) readonly disabled: boolean;
    @Prop({ default: () => [] }) readonly rulesProp: Array<any>;
    @Prop({ default: undefined }) readonly maxDay?: string;
    @Prop({ default: undefined }) readonly minDay?: string;
    @Prop({ default: () => [] }) readonly errorMessages: Array<any>;

    @Watch('hour')
    onHourChanged(val: string): void {
        this.displayHour = val;
    }
    @Watch('minute')
    onMinuteChanged(val: string): void {
        this.displayMinute = val;
    }
    @Emit()
    public input(value: Date) {}
    @Emit('update:display-day')
    public updateDisplayDay(value: string) {}
    @Emit('update:display-hour')
    public updateDisplayHour(value: string) {}
    @Emit('update:display-minute')
    public updateDisplayMinute(value: string) {}

    displayDay: string | null = null;
    displayHour: string | null = null;
    displayMinute: string | null = null;

    calenderError: boolean = false;

    timeError: boolean = false;

    isDayFocused: boolean = false;
    isTimeFocused: boolean = false;

    /*
     * setter, getter
     */
    get getRules() {
        if (this.calenderError || this.timeError) {
            return [(this.rules as any).alwaysValid];
        } else {
            return this.rulesProp;
        }
    }
    get isDisabled(): boolean {
        return this.disabled;
    }
    get day(): string {
        if (!this.value) {
            return this.displayDay;
        }
        return dayjs(this.value).format('YYYY-MM-DD');
    }
    set day(val: string) {
        this.displayDay = val;
        this.updateDisplayDay(val);
        this.updateValue(val, this.displayHour, this.displayMinute);
    }
    get time(): string {
        if (!this.value) {
            return '00:00';
        }
        return `${this.hour}:${this.minute}`;
    }
    set time(val: string) {
        let splitted = val.split(':');
        this.hour = splitted[0];
        this.minute = splitted[1];
    }
    get hour(): string {
        if (!this.value) {
            return this.displayHour;
        }
        return ('0' + this.value.getHours()).slice(-2);
    }
    set hour(val: string) {
        this.displayHour = val;
        this.updateDisplayHour(val);
        this.updateValue(this.displayDay, val, this.displayMinute);
    }
    get minute(): string {
        if (!this.value) {
            return this.displayMinute;
        }
        return ('0' + this.value.getMinutes()).slice(-2);
    }
    set minute(val: string) {
        this.displayMinute = val;
        this.updateDisplayMinute(val);
        this.updateValue(this.displayDay, this.displayHour, val);
    }
    get calenderMax(): string | undefined {
        return this.validatedDate(this.maxDay);
    }
    get calenderMin(): string | undefined {
        return this.validatedDate(this.minDay);
    }
    // IE11だとfocusとblurが思ったような順番で来ないので、個々のコンポーネントの状態で判定する
    get isFocused(): boolean {
        return this.isDayFocused || this.isTimeFocused;
    }

    @Watch('isFocused')
    onFocusChange() {
        const elm = (this as any).$refs['v-input'];
        if (!elm) {
            return;
        }
        elm.isFocused = this.isFocused;
    }

    /*
     * methods
     */
    updateValue(d: string, h: string, m: string) {
        const date = dayjs(d, 'YYYY-MM-DD', true);
        if (!date.isValid()) {
            this.input(undefined);
            return;
        }
        const hour = parseInt(h);
        if (isNaN(hour)) {
            this.input(undefined);
            return;
        }
        const minute = parseInt(m);
        if (isNaN(minute)) {
            this.input(undefined);
            return;
        }

        const newVal = date.hour(hour).minute(minute).toDate();

        this.input(newVal);
    }
    validatedDate(day?: string): string | undefined {
        if (!day) {
            return undefined;
        }
        const isValid = dayjs(day, 'YYYY-MM-DD', true).isValid();
        if (!isValid) {
            return undefined;
        }
        return day;
    }
}
</script>
